import React, { useCallback, useEffect, useRef, useState } from "react";

import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  Divider,
  Fade,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Typography,
} from "@mui/material";

import {
  CalendarMonth,
  CheckCircleOutline,
  Close,
  CloudUploadOutlined,
  InfoOutlined,
  Publish,
  School,
  Unpublished,
  WarningAmberOutlined,
} from "@mui/icons-material";

import api from "../api/api";
import { useAcademicYear } from "../AcademicYearContext";

/* ============================================================
   DESIGN SYSTEM
============================================================ */

const COLORS = {
  deepNavy: "#071A2F",
  navy: "#0B2748",
  royalBlue: "#123F73",
  blue: "#1D5FA7",

  maroon: "#800000",
  deepMaroon: "#5C0000",

  gold: "#D6A928",
  brightGold: "#F2C94C",
  softGold: "#FFF8DE",

  white: "#FFFFFF",
  background: "#F4F7FB",
  surface: "#FFFFFF",

  text: "#172033",
  muted: "#667085",
  border: "#D9E2EC",

  success: "#087443",
  successSoft: "#ECFDF3",

  danger: "#B42318",
  dangerSoft: "#FFF1F0",

  warning: "#A15C00",
  warningSoft: "#FFF7E6",

  info: "#175CD3",
  infoSoft: "#EFF8FF",
};

/* ============================================================
   HELPERS
============================================================ */

const getErrorMessage = (error, fallback) => {
  const data = error?.response?.data;

  if (!data) {
    return fallback;
  }

  if (typeof data === "string") {
    return data;
  }

  if (data.error) {
    return data.error;
  }

  if (data.message) {
    return data.message;
  }

  if (data.detail) {
    return data.detail;
  }

  if (Array.isArray(data.non_field_errors) && data.non_field_errors.length) {
    return data.non_field_errors[0];
  }

  const firstFieldError = Object.values(data).find((value) => {
    return Array.isArray(value) && value.length > 0;
  });

  if (firstFieldError) {
    return firstFieldError[0];
  }

  return fallback;
};

/* ============================================================
   COMPONENT
============================================================ */

const AdminPublishGradesPopup = ({ open, onClose }) => {
  const { selectedYear } = useAcademicYear();

  const [periods, setPeriods] = useState([]);
  const [selectedPeriod, setSelectedPeriod] = useState("");

  const [loading, setLoading] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [processingAction, setProcessingAction] = useState("");

  const [message, setMessage] = useState({
    type: "",
    text: "",
  });

  const messageTimerRef = useRef(null);

  const token = localStorage.getItem("access");

  /* ==========================================================
     MESSAGE HANDLING
  ========================================================== */

  const clearMessage = useCallback(() => {
    setMessage({
      type: "",
      text: "",
    });
  }, []);

  const showMessage = useCallback((type, text) => {
    setMessage({
      type,
      text,
    });

    if (messageTimerRef.current) {
      clearTimeout(messageTimerRef.current);
    }

    messageTimerRef.current = setTimeout(() => {
      setMessage({
        type: "",
        text: "",
      });
    }, 6000);
  }, []);

  /* ==========================================================
     CLEANUP
  ========================================================== */

  useEffect(() => {
    return () => {
      if (messageTimerRef.current) {
        clearTimeout(messageTimerRef.current);
      }
    };
  }, []);

  /* ==========================================================
     RESET WHEN CLOSED
  ========================================================== */

  useEffect(() => {
    if (!open) {
      setSelectedPeriod("");
      clearMessage();
      setProcessing(false);
      setProcessingAction("");
    }
  }, [open, clearMessage]);

  /* ==========================================================
     FETCH PERIODS
  ========================================================== */

  useEffect(() => {
    if (!open) {
      return;
    }

    let mounted = true;

    const fetchPeriods = async () => {
      setLoading(true);
      clearMessage();

      try {
        const response = await api.get("/api/users/periods/", {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (!mounted) {
          return;
        }

        const fetchedPeriods = Array.isArray(response.data)
          ? response.data
          : Array.isArray(response.data?.results)
          ? response.data.results
          : [];

        setPeriods(fetchedPeriods);

        if (fetchedPeriods.length === 0) {
          showMessage(
            "warning",
            "No academic periods are currently available."
          );
        }
      } catch (error) {
        if (!mounted) {
          return;
        }

        setPeriods([]);

        showMessage(
          "error",
          getErrorMessage(error, "Failed to fetch academic periods.")
        );
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    fetchPeriods();

    return () => {
      mounted = false;
    };
  }, [open, token, clearMessage, showMessage]);

  /* ==========================================================
     COMMON REQUEST HANDLER
  ========================================================== */

  const processGrades = async (action) => {
    if (!selectedPeriod) {
      showMessage(
        "error",
        "Please select the academic period you want to process."
      );
      return;
    }

    if (!selectedYear?.id) {
      showMessage(
        "error",
        "No active academic year is selected. Please select an academic year first."
      );
      return;
    }

    if (processing) {
      return;
    }

    const isPublishing = action === "publish";

    setProcessing(true);
    setProcessingAction(action);
    clearMessage();

    const endpoint = isPublishing
      ? "/api/users/student-averages/publish-grades/"
      : "/api/users/student-averages/unpublish-grades/";

    try {
      const response = await api.post(
        endpoint,
        {
          period: selectedPeriod,
          academic_year: selectedYear.id,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      showMessage(
        "success",
        response?.data?.message ||
          (isPublishing
            ? "Grades were published successfully."
            : "Grades were unpublished successfully.")
      );
    } catch (error) {
      showMessage(
        "error",
        getErrorMessage(
          error,
          isPublishing
            ? "Failed to publish grades."
            : "Failed to unpublish grades."
        )
      );
    } finally {
      setProcessing(false);
      setProcessingAction("");
    }
  };

  /* ==========================================================
     ACTIONS
  ========================================================== */

  const publishGrades = () => {
    processGrades("publish");
  };

  const unpublishGrades = () => {
    processGrades("unpublish");
  };

  const selectedPeriodObject = periods.find(
    (period) => String(period.id) === String(selectedPeriod)
  );

  const hasAcademicYear = Boolean(selectedYear?.id);
  const hasPeriod = Boolean(selectedPeriod);
  const canProcess = hasAcademicYear && hasPeriod && !processing;

  /* ==========================================================
     RENDER
  ========================================================== */

  return (
    <Dialog
      open={open}
      onClose={processing ? undefined : onClose}
      fullWidth
      maxWidth="sm"
      TransitionComponent={Fade}
      keepMounted
      PaperProps={{
        sx: {
          width: "100%",
          maxWidth: 620,
          margin: {
            xs: 1,
            sm: 2,
          },
          borderRadius: {
            xs: 3,
            sm: 4,
          },
          overflow: "hidden",
          backgroundColor: COLORS.surface,
          border: `1px solid ${COLORS.border}`,
          boxShadow: "0 30px 80px rgba(7, 26, 47, 0.28)",
        },
      }}
      slotProps={{
        backdrop: {
          sx: {
            backgroundColor: "rgba(3, 16, 31, 0.72)",
            backdropFilter: "blur(5px)",
          },
        },
      }}
    >
      {/* ======================================================
          HEADER
      ====================================================== */}

      <Box
        sx={{
          position: "relative",
          overflow: "hidden",
          background: `linear-gradient(135deg, ${COLORS.deepNavy} 0%, ${COLORS.navy} 58%, ${COLORS.maroon} 150%)`,
          color: COLORS.white,
          px: {
            xs: 2.25,
            sm: 3.5,
          },
          py: {
            xs: 2.5,
            sm: 3,
          },
        }}
      >
        {/* Decorative glow */}
        <Box
          sx={{
            position: "absolute",
            width: 180,
            height: 180,
            borderRadius: "50%",
            background: "rgba(242, 201, 76, 0.08)",
            top: -100,
            right: -50,
            pointerEvents: "none",
          }}
        />

        <Box
          sx={{
            position: "absolute",
            width: 120,
            height: 120,
            borderRadius: "50%",
            background: "rgba(255,255,255,0.04)",
            bottom: -80,
            left: -40,
            pointerEvents: "none",
          }}
        />

        <IconButton
          onClick={onClose}
          disabled={processing}
          aria-label="Close grade publication dialog"
          sx={{
            position: "absolute",
            right: 10,
            top: 10,
            color: "rgba(255,255,255,0.82)",
            backgroundColor: "rgba(255,255,255,0.08)",
            "&:hover": {
              backgroundColor: "rgba(255,255,255,0.16)",
              color: COLORS.white,
            },
          }}
        >
          <Close />
        </IconButton>

        <Stack
          direction="row"
          spacing={2}
          alignItems="center"
          sx={{
            position: "relative",
            zIndex: 1,
            pr: 5,
          }}
        >
          <Box
            sx={{
              width: {
                xs: 52,
                sm: 60,
              },
              height: {
                xs: 52,
                sm: 60,
              },
              flexShrink: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              borderRadius: 2.5,
              backgroundColor: "rgba(255,255,255,0.11)",
              border: "1px solid rgba(242,201,76,0.42)",
              boxShadow: "inset 0 0 0 1px rgba(255,255,255,0.04)",
            }}
          >
            <School
              sx={{
                fontSize: {
                  xs: 28,
                  sm: 32,
                },
                color: COLORS.brightGold,
              }}
            />
          </Box>

          <Box sx={{ minWidth: 0 }}>
            <Typography
              sx={{
                fontSize: {
                  xs: 11,
                  sm: 12,
                },
                fontWeight: 800,
                letterSpacing: "0.16em",
                textTransform: "uppercase",
                color: COLORS.brightGold,
                mb: 0.4,
              }}
            >
              Academic Administration
            </Typography>

            <Typography
              sx={{
                fontSize: {
                  xs: 21,
                  sm: 25,
                },
                lineHeight: 1.15,
                fontWeight: 800,
                letterSpacing: "-0.02em",
              }}
            >
              Grade Publication Control
            </Typography>

            <Typography
              sx={{
                mt: 0.65,
                fontSize: {
                  xs: 12,
                  sm: 13,
                },
                color: "rgba(255,255,255,0.72)",
                lineHeight: 1.5,
              }}
            >
              Manage the visibility of student academic results.
            </Typography>
          </Box>
        </Stack>
      </Box>

      {/* ======================================================
          CONTENT
      ====================================================== */}

      <DialogContent
        sx={{
          p: {
            xs: 2,
            sm: 3.5,
          },
          backgroundColor: COLORS.background,
        }}
      >
        {/* ====================================================
            STATUS MESSAGE
        ==================================================== */}

        <Fade in={Boolean(message.text)} mountOnEnter unmountOnExit>
          <Box sx={{ mb: 2.5 }}>
            <Alert
              severity={
                message.type === "success"
                  ? "success"
                  : message.type === "warning"
                  ? "warning"
                  : "error"
              }
              icon={
                message.type === "success" ? (
                  <CheckCircleOutline />
                ) : message.type === "warning" ? (
                  <WarningAmberOutlined />
                ) : (
                  <InfoOutlined />
                )
              }
              onClose={clearMessage}
              sx={{
                borderRadius: 2.5,
                border:
                  message.type === "success"
                    ? `1px solid #A6E6C7`
                    : message.type === "warning"
                    ? `1px solid #F3D49A`
                    : `1px solid #F1B8B3`,
                backgroundColor:
                  message.type === "success"
                    ? COLORS.successSoft
                    : message.type === "warning"
                    ? COLORS.warningSoft
                    : COLORS.dangerSoft,
                color:
                  message.type === "success"
                    ? COLORS.success
                    : message.type === "warning"
                    ? COLORS.warning
                    : COLORS.danger,
                "& .MuiAlert-icon": {
                  color:
                    message.type === "success"
                      ? COLORS.success
                      : message.type === "warning"
                      ? COLORS.warning
                      : COLORS.danger,
                },
              }}
            >
              <Typography
                sx={{
                  fontSize: 13.5,
                  fontWeight: 600,
                  lineHeight: 1.5,
                }}
              >
                {message.text}
              </Typography>
            </Alert>
          </Box>
        </Fade>

        {/* ====================================================
            ACADEMIC YEAR
        ==================================================== */}

        <Box
          sx={{
            border: `1px solid ${
              hasAcademicYear ? "#C8D9EA" : "#F0D6A5"
            }`,
            borderRadius: 3,
            backgroundColor: hasAcademicYear
              ? COLORS.white
              : COLORS.warningSoft,
            overflow: "hidden",
            mb: 2.5,
          }}
        >
          <Box
            sx={{
              height: 4,
              background: hasAcademicYear
                ? `linear-gradient(90deg, ${COLORS.blue}, ${COLORS.brightGold})`
                : `linear-gradient(90deg, #D98B00, ${COLORS.brightGold})`,
            }}
          />

          <Box
            sx={{
              p: {
                xs: 2,
                sm: 2.25,
              },
              display: "flex",
              alignItems: "center",
              gap: 1.5,
            }}
          >
            <Box
              sx={{
                width: 42,
                height: 42,
                flexShrink: 0,
                borderRadius: 2,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: hasAcademicYear
                  ? COLORS.softGold
                  : "#FFF0D0",
                color: hasAcademicYear
                  ? COLORS.maroon
                  : COLORS.warning,
              }}
            >
              <CalendarMonth fontSize="small" />
            </Box>

            <Box sx={{ minWidth: 0 }}>
              <Typography
                sx={{
                  fontSize: 11,
                  fontWeight: 800,
                  letterSpacing: "0.08em",
                  textTransform: "uppercase",
                  color: COLORS.muted,
                  mb: 0.25,
                }}
              >
                Active Academic Year
              </Typography>

              {hasAcademicYear ? (
                <Typography
                  sx={{
                    fontSize: {
                      xs: 15,
                      sm: 16,
                    },
                    fontWeight: 800,
                    color: COLORS.text,
                    wordBreak: "break-word",
                  }}
                >
                  {selectedYear.name}
                </Typography>
              ) : (
                <Typography
                  sx={{
                    fontSize: 14,
                    fontWeight: 700,
                    color: COLORS.warning,
                  }}
                >
                  No academic year selected
                </Typography>
              )}
            </Box>
          </Box>
        </Box>

        {/* ====================================================
            INSTRUCTION
        ==================================================== */}

        <Box sx={{ mb: 2.5 }}>
          <Typography
            sx={{
              fontSize: 13,
              fontWeight: 800,
              color: COLORS.text,
              mb: 0.55,
            }}
          >
            Select the reporting period
          </Typography>

          <Typography
            sx={{
              fontSize: 12.5,
              lineHeight: 1.6,
              color: COLORS.muted,
            }}
          >
            Choose the period whose student grades should be made visible or
            hidden from authorized users.
          </Typography>
        </Box>

        {/* ====================================================
            PERIOD SELECT
        ==================================================== */}

        <FormControl fullWidth disabled={loading || processing}>
          <InputLabel id="grade-publication-period-label">
            Academic Period
          </InputLabel>

          <Select
            labelId="grade-publication-period-label"
            value={selectedPeriod}
            label="Academic Period"
            onChange={(event) => {
              setSelectedPeriod(event.target.value);
              clearMessage();
            }}
            MenuProps={{
              PaperProps: {
                sx: {
                  mt: 0.5,
                  borderRadius: 2,
                  border: `1px solid ${COLORS.border}`,
                  boxShadow: "0 14px 40px rgba(7, 26, 47, 0.16)",
                },
              },
            }}
            sx={{
              backgroundColor: COLORS.white,
              borderRadius: 2.5,
              minHeight: 56,

              "& .MuiOutlinedInput-notchedOutline": {
                borderColor: COLORS.border,
              },

              "&:hover .MuiOutlinedInput-notchedOutline": {
                borderColor: COLORS.blue,
              },

              "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                borderColor: COLORS.maroon,
                borderWidth: 2,
              },
            }}
          >
            {loading ? (
              <MenuItem disabled>
                <Stack
                  direction="row"
                  spacing={1.25}
                  alignItems="center"
                >
                  <CircularProgress
                    size={18}
                    sx={{ color: COLORS.maroon }}
                  />

                  <Typography sx={{ fontSize: 13 }}>
                    Loading academic periods...
                  </Typography>
                </Stack>
              </MenuItem>
            ) : periods.length > 0 ? (
              periods.map((period) => (
                <MenuItem
                  key={period.id}
                  value={period.id}
                  sx={{
                    py: 1.25,
                    fontSize: 14,
                    fontWeight: 600,
                    whiteSpace: "normal",
                  }}
                >
                  {period.name}
                </MenuItem>
              ))
            ) : (
              <MenuItem disabled>
                <Typography
                  sx={{
                    fontSize: 13,
                    color: COLORS.muted,
                  }}
                >
                  No academic periods found
                </Typography>
              </MenuItem>
            )}
          </Select>
        </FormControl>

        {/* ====================================================
            SELECTED PERIOD PREVIEW
        ==================================================== */}

        {selectedPeriodObject && (
          <Fade in>
            <Box
              sx={{
                mt: 1.5,
                px: 1.75,
                py: 1.25,
                borderRadius: 2,
                backgroundColor: COLORS.softBlue,
                border: "1px solid #CFE0F2",
                display: "flex",
                alignItems: "center",
                gap: 1.25,
              }}
            >
              <CheckCircleOutline
                sx={{
                  color: COLORS.success,
                  fontSize: 19,
                  flexShrink: 0,
                }}
              />

              <Typography
                sx={{
                  fontSize: 12.5,
                  color: COLORS.royalBlue,
                  lineHeight: 1.5,
                }}
              >
                Selected period:{" "}
                <Box
                  component="span"
                  sx={{
                    fontWeight: 800,
                  }}
                >
                  {selectedPeriodObject.name}
                </Box>
              </Typography>
            </Box>
          </Fade>
        )}

        {/* ====================================================
            DIVIDER
        ==================================================== */}

        <Divider
          sx={{
            my: {
              xs: 2.75,
              sm: 3,
            },
            borderColor: COLORS.border,
          }}
        />

        {/* ====================================================
            ACTION AREA
        ==================================================== */}

        <Box>
          <Typography
            sx={{
              fontSize: 13,
              fontWeight: 800,
              color: COLORS.text,
              mb: 0.55,
            }}
          >
            Publication action
          </Typography>

          <Typography
            sx={{
              fontSize: 12.5,
              lineHeight: 1.6,
              color: COLORS.muted,
              mb: 2,
            }}
          >
            Publishing makes the selected period's grades available according
            to your application's grade visibility rules. Unpublishing
            reverses that publication state.
          </Typography>

          <Stack
            direction={{
              xs: "column",
              sm: "row",
            }}
            spacing={1.5}
          >
            {/* PUBLISH */}

            <Button
              fullWidth
              variant="contained"
              disableElevation
              startIcon={
                processing && processingAction === "publish" ? (
                  <CircularProgress
                    size={19}
                    thickness={4}
                    color="inherit"
                  />
                ) : (
                  <CloudUploadOutlined />
                )
              }
              onClick={publishGrades}
              disabled={!canProcess}
              sx={{
                minHeight: 52,
                borderRadius: 2.5,
                textTransform: "none",
                fontSize: 14,
                fontWeight: 800,
                letterSpacing: "0.01em",

                background: `linear-gradient(135deg, ${COLORS.maroon}, ${COLORS.deepMaroon})`,

                "&:hover": {
                  background: `linear-gradient(135deg, #970000, ${COLORS.maroon})`,
                  boxShadow: "0 10px 24px rgba(128,0,0,0.24)",
                },

                "&:disabled": {
                  backgroundColor: "#D6D9DE",
                  color: "#8A9099",
                },
              }}
            >
              {processing && processingAction === "publish"
                ? "Publishing..."
                : "Publish Grades"}
            </Button>

            {/* UNPUBLISH */}

            <Button
              fullWidth
              variant="outlined"
              startIcon={
                processing && processingAction === "unpublish" ? (
                  <CircularProgress
                    size={19}
                    thickness={4}
                    color="inherit"
                  />
                ) : (
                  <Unpublished />
                )
              }
              onClick={unpublishGrades}
              disabled={!canProcess}
              sx={{
                minHeight: 52,
                borderRadius: 2.5,
                textTransform: "none",
                fontSize: 14,
                fontWeight: 800,
                letterSpacing: "0.01em",

                color: COLORS.deepNavy,
                borderColor: "#B8C4D1",
                backgroundColor: COLORS.white,

                "&:hover": {
                  borderColor: COLORS.deepNavy,
                  backgroundColor: "#F3F6F9",
                },

                "&:disabled": {
                  borderColor: "#D6D9DE",
                  color: "#9AA1AA",
                },
              }}
            >
              {processing && processingAction === "unpublish"
                ? "Unpublishing..."
                : "Unpublish Grades"}
            </Button>
          </Stack>
        </Box>

        {/* ====================================================
            ADMIN NOTICE
        ==================================================== */}

        <Box
          sx={{
            mt: 2.5,
            p: 1.75,
            borderRadius: 2.5,
            backgroundColor: "#F8FAFC",
            border: `1px solid ${COLORS.border}`,
            display: "flex",
            gap: 1.25,
            alignItems: "flex-start",
          }}
        >
          <InfoOutlined
            sx={{
              color: COLORS.blue,
              fontSize: 19,
              mt: 0.1,
              flexShrink: 0,
            }}
          />

          <Box>
            <Typography
              sx={{
                fontSize: 12.5,
                fontWeight: 800,
                color: COLORS.text,
                mb: 0.35,
              }}
            >
              Administrative control
            </Typography>

            <Typography
              sx={{
                fontSize: 12,
                lineHeight: 1.6,
                color: COLORS.muted,
              }}
            >
              Confirm the academic year and period carefully before changing
              publication status. The selected period is processed using the
              academic year currently active in the system.
            </Typography>
          </Box>
        </Box>



      </DialogContent>

      {/* ======================================================
          PROCESSING OVERLAY
      ====================================================== */}

      {processing && (
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            zIndex: 20,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "rgba(255,255,255,0.42)",
            backdropFilter: "blur(1.5px)",
            pointerEvents: "none",
          }}
        >
          <Box
            sx={{
              px: 2.5,
              py: 1.75,
              borderRadius: 3,
              backgroundColor: "rgba(7,26,47,0.94)",
              color: COLORS.white,
              boxShadow: "0 18px 45px rgba(0,0,0,0.24)",
              display: "flex",
              alignItems: "center",
              gap: 1.5,
            }}
          >
            <CircularProgress
              size={22}
              thickness={4}
              sx={{
                color: COLORS.brightGold,
              }}
            />

            <Typography
              sx={{
                fontSize: 13,
                fontWeight: 700,
              }}
            >
              {processingAction === "publish"
                ? "Publishing grades..."
                : "Unpublishing grades..."}
            </Typography>
          </Box>
        </Box>
      )}
    </Dialog>
  );
};

export default AdminPublishGradesPopup;